import * as React from "react";
import {
  styled,
  useTheme,
  ThemeProvider,
  createTheme,
} from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import MuiAppBar from "@mui/material/AppBar";
import MuiToolbar from "@mui/material/Toolbar"; // Use a different name to avoid conflict
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import LogoutIcon from "@mui/icons-material/Logout";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import { Badge } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FiberManualRecordOutlinedIcon from "@mui/icons-material/FiberManualRecordOutlined";
import axios from "axios";
import { toast } from "react-toastify";
import { getName } from "./Other/common";
import { menuSections } from "./menuConfig";
import { menuSectionsTwo } from "./menuSectionsTwo";
import AccountMenu from "./Other/AccountMenu";
import useIdleTimer from "./Other/useIdleTimer";

const drawerWidth = 280;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  })
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const Toolbar = styled(MuiToolbar)(({ theme }) => ({
  minHeight: "48px", // Reduced from default 64px
  padding: theme.spacing(0, 1), // Adjust padding as needed
}));

const baseTheme = {
  typography: {
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    h6: {
      fontWeight: 600,
    },
    // Add this to make the font size smaller for menu items
    body1: {
      fontSize: "0.875rem", // Equivalent to 14px
    },
  },
  components: {
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
          // Reduce padding to make items more compact
          paddingTop: 4,
          paddingBottom: 4,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 36, // Slightly reduce the icon width
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: "40px", // Reduce the overall height
          "& .MuiAccordionSummary-content": {
            margin: "5px 0", // Adjust vertical margins
          },
        },
        content: {
          "& .MuiTypography-root": {
            fontSize: "0.875rem", // Make the text smaller (14px)
          },
        },
      },
    },
  },
};

const lightTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: "light",
    primary: {
      main: "#009879",
    },
    secondary: {
      main: "#f50057",
    },
    background: {
      default: "#f5f5f5",
      paper: "#ffffff",
    },
    text: {
      primary: "#000000",
    },
  },
});

const darkTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: "dark",
    primary: {
      main: "#859099",
    },
    secondary: {
      main: "#f48fb1",
    },
    background: {
      default: "#303030",
      paper: "#424242",
    },
    text: {
      primary: "#ffffff",
    },
  },
});

export default function PersistentDrawerLeft() {
  useIdleTimer(120000)
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [darkMode, setDarkMode] = React.useState(() => {
    const savedMode = localStorage.getItem("darkMode");
    return savedMode ? JSON.parse(savedMode) : false;
  });
  const [anchorEl, setAnchorEl] = React.useState(null);
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = React.useState(0);
  const location = useLocation();

  const [userRole, setUserRole] = React.useState(() => {
    const adminInfo = localStorage.getItem("admin-info");
    if (adminInfo) {
      const parsedInfo = JSON.parse(adminInfo);
      return parsedInfo.Web_MenuAccess || "";
    }
    return "";
  });

  React.useEffect(() => {
    const handleStorageChange = () => {
      const adminInfo = localStorage.getItem("admin-info");
      if (adminInfo) {
        const parsedInfo = JSON.parse(adminInfo);
        setUserRole(parsedInfo.UserRole || "");
      } else {
        setUserRole("");
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  React.useEffect(() => {
    // ... existing code ...

    // Find and expand the section containing the active menu item
    const activeSection = menuSectionsTwo.find((section) =>
      section.items.some((item) => item.path === location.pathname)
    );
    if (activeSection) {
      setExpandedSection(activeSection.title);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
    // fetchPendingCount();
  }, [darkMode]);

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);
  const toggleDarkMode = () => setDarkMode(!darkMode);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const fetchPendingCount = async () => {
    try {
      const response = await axios.post(
        "http://localhost:8000/api/visit/getPendingVisitor"
      );
      setPendingCount(response.data.response.recordset[0].PendingVisitorsCount);
    } catch (error) {
      console.error("Error fetching pending count:", error);
      toast.error(error);
    }
  };

  const goto = (path) => {
    const adminInfo = localStorage.getItem("admin-info");
    if (adminInfo) {
      const { Web_MenuAccess } = JSON.parse(adminInfo);
      const userRoles = Web_MenuAccess.split(",").map((role) => role.trim());

      const isPathAllowed = menuSectionsTwo.some((section) =>
        section.items.some(
          (item) => item.path === path && userRoles.includes(item.role)
        )
      );

      if (isPathAllowed) {
        navigate(path);
      } else {
        navigate("/dashboard/dashbordhome");
        toast.error("You don't have permission to access this page");
      }
    } else {
      navigate("/");
    }
  };

  const logout = () => {
    localStorage.clear();
    navigate("/");
  };

  const filterMenuItems = (items, userRole) => {
    if (!userRole) return [];
    const userRoles = userRole.split(",").map((role) => role.trim());
    return items.filter((item) => userRoles.includes(item.role));
  };

  const [expandedSection, setExpandedSection] = React.useState(null);
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedSection(isExpanded ? panel : null);
  };

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar position="fixed" open={open} elevation={0}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{ mr: 2, ...(open && { display: "none" }) }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ flexGrow: 1 }}
            >
              ATS
            </Typography>
            <Typography>Hello {getName()?.Name||""}</Typography>
            {/* <IconButton color="inherit" onClick={handleMenuOpen}>
              <AccountCircleIcon />
            </IconButton> */}
            
            <IconButton color="inherit" onClick={toggleDarkMode}>
              {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            <AccountMenu />
            <IconButton
              color="inherit"
              onClick={() => goto("/dashboard/pendinglist")}
            >
              {/* <Badge badgeContent={pendingCount} color="secondary">
                <PendingActionsIcon />
              </Badge> */}
            </IconButton>
          </Toolbar>
        </AppBar>
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              bgcolor: "background.default",
            },
          }}
          variant="persistent"
          anchor="left"
          open={open}
        >
          <DrawerHeader>
            <Typography
              variant="h6"
              noWrap
              component="div"
              marginRight={3}
              sx={{ color: "#009879" }}
            >
              ASSET TRACKING
            </Typography>
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === "ltr" ? (
                <ChevronLeftIcon />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List>
            {menuSectionsTwo.map((section) => (
              <Accordion
                key={section.title}
                expanded={expandedSection === section.title}
                onChange={handleAccordionChange(section.title)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${section.title}-content`}
                  id={`${section.title}-header`}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {section.icon && <section.icon sx={{}} />}
                    <Typography ml={5}>{section.title}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {filterMenuItems(section.items, userRole).map((item) => (
                      <ListItemButton
                        key={item.text}
                        onClick={() => goto(item.path)}
                        selected={location.pathname === item.path}
                        sx={{
                          py: 0.2,
                          borderRadius: 1.5,
                          "&:hover": {
                            bgcolor: "#e8c5e1", // Light cyan for hover state
                          },
                          "&.Mui-selected": {
                            bgcolor: "#009879", // Teal for selected state
                            "&:hover": {
                              bgcolor: "#b1c3c0", // Darker teal for hover on selected
                            },
                          },
                        }}
                      >
                        <ListItemIcon>
                          {" "}
                          {location.pathname === item.path ? (
                            <FiberManualRecordIcon fontSize="small" />
                          ) : (
                            <FiberManualRecordOutlinedIcon fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: "0.875rem",
                            fontWeight:
                              location.pathname === item.path
                                ? "bold"
                                : "normal",
                            color:
                              location.pathname === item.path
                                ? "white"
                                : "#000000", // Black for selected, grey for others
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </List>
          <Divider sx={{ marginTop: 2 }} />
          {/* <Typography v>Bartech</Typography> */}
          <List sx={{ marginTop: "auto" }}>
            <ListItemButton onClick={logout} sx={{ py: 1.5 }}>
              <ListItemIcon sx={{ color: "secondary.main" }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText sx={{ marginLeft: 5 }} primary="Logout" />
            </ListItemButton>
          </List>
        </Drawer>
        <Main open={open}>
          <DrawerHeader />
          <Box sx={{ minHeight: "calc(100vh - 64px - 48px)" }}>
            <Outlet />
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ py: 2 }}
          >
            &copy; {new Date().getFullYear()} Bartech Data Systems Pvt. Ltd. All
            rights reserved
          </Typography>
        </Main>
      </Box>
    </ThemeProvider>
  );
}
